import { SortOrder } from '@synconset/sorting';
import { CmEpisodicItemPicture } from '../..';
import ChangeEpisodicItem from '../change-episodic-items/change-episodic-item.entity';
import Change from '../changes/change.entity';
import CmWrapBoxEpisodicItem from '../cm-wrap-box-episodic-items/cm-wrap-box-episodic-item.entity';
import CmWrapBox from '../cm-wrap-boxes/cm-wrap-box.entity';
import { DEPARTMENTS } from '../enums/enums';
import EpisodicCharacter from '../episodic-characters/episodic-character.entity';
import EpisodicItem from '../episodic-items/episodic-item.entity';
import EpisodicProp from '../episodic-props/episodic-prop.entity';
import Inventory from '../inventory/inventory.entity';
import PrEpisodicPropPicture from '../pr-episodic-prop-pictures/pr-episodic-prop-picture.entity';
import PrWrapBoxEpisodicProp from '../pr-wrap-box-episodic-props/pr-wrap-box-episodic-prop.entity';
import PrWrapBox from '../pr-wrap-boxes/pr-wrap-box.entity';
import PropSceneCharScene from '../prop-scene-char-scenes/prop-scene-char-scene.entity';
import PropScene from '../prop-scenes/prop-scene.entity';
import SetPiecePicture from '../set-piece-pictures/set-piece-picture.entity';
import SetPieceSet from '../set-piece-sets/set-piece-set.entity';
import SetPiece from '../set-pieces/set-piece.entity';
import SetWrapBoxSetPiece from '../set-wrap-box-set-pieces/set-wrap-box-set-piece.entity';
import SetWrapBox from '../set-wrap-boxes/set-wrap-box.entity';
import Set from '../sets/set.entity';
import { SosDeleteResponse, SosListResponse, SosUpdateResponse } from '../sos-response';
import { AnyInventory, AnyWrapBox, InventoryDepartment } from '../types';

export interface InventoryListResponse extends SosListResponse {
	listed: {
		Change: Change[];
		ChangeEpisodicItem: ChangeEpisodicItem[];
		CmWrapBox: CmWrapBox[];
		CmWrapBoxEpisodicItem: CmWrapBoxEpisodicItem[];
		EpisodicItem: EpisodicItem[];
		EpisodicProp: EpisodicProp[];
		Inventory: Inventory[];
		PrWrapBox: PrWrapBox[];
		PrWrapBoxEpisodicProp: PrWrapBoxEpisodicProp[];
		Set: Set[];
		SetPiece: SetPiece[];
		SetPieceSet: SetPieceSet[];
		SetWrapBox: SetWrapBox[];
		SetWrapBoxSetPiece: SetWrapBoxSetPiece[];
	};
	totalCount: number;
}

export interface InventoryUpdateResponse extends SosUpdateResponse {
	created: {
		PropSceneCharScene: PropSceneCharScene[];
		SetPieceSet?: SetPieceSet[];
	};
	destroyed: {
		CmWrapBoxEpisodicItem: CmWrapBoxEpisodicItem[];
		PropSceneCharScene: PropSceneCharScene[];
		PrWrapBoxEpisodicProp: PrWrapBoxEpisodicProp[];
		SetPieceSet?: SetPieceSet[];
		SetWrapBoxSetPiece: SetWrapBoxSetPiece[];
	};
	updated: {
		CmWrapBoxEpisodicItem: CmWrapBoxEpisodicItem[];
		EpisodicItem: EpisodicItem[];
		EpisodicProp: EpisodicProp[];
		PropScene: PropScene[];
		PrWrapBoxEpisodicProp: PrWrapBoxEpisodicProp[];
		SetPiece: SetPiece[];
		SetWrapBoxSetPiece: SetWrapBoxSetPiece[];
	};
}

export interface InventoryRemoveFromWrapBoxResponse extends SosDeleteResponse {
	destroyed: {
		CmWrapBoxEpisodicItem?: CmWrapBoxEpisodicItem[];
		PrWrapBoxEpisodicProp?: PrWrapBoxEpisodicProp[];
		SetWrapBoxSetPiece?: SetWrapBoxSetPiece[];
	};
}

export interface InventoryUpdatePayload {
	inventory: Partial<AnyInventory>[];
	propScenes?: Partial<PropScene>[];
	propSceneCharScenes?: Partial<PropSceneCharScene>[];
	cmWrapBoxItems?: Partial<CmWrapBoxEpisodicItem>[];
	prWrapBoxProps?: Partial<PrWrapBoxEpisodicProp>[];
	setWrapBoxPieces?: Partial<SetWrapBoxSetPiece>[];
	setPieceSets?: Partial<SetPieceSet>[];
}

export interface WrapBoxInventoryUpdatePayload {
	wrapBoxes?: Partial<AnyWrapBox>[];
	cmWrapBoxItems?: Partial<CmWrapBoxEpisodicItem>[];
	prWrapBoxProps?: Partial<PrWrapBoxEpisodicProp>[];
	setWrapBoxPieces?: Partial<SetWrapBoxSetPiece>[];
}

export type WrapBoxInventoryCreatePayload = WrapBoxInventoryUpdatePayload;

export interface DeptSpecificInventoryCreateResponse<
	T extends InventoryDepartment
	> {
	created: T extends DEPARTMENTS.CM
	? {
		EpisodicItem: EpisodicItem[];
		CmWrapBoxEpisodicItem: CmWrapBoxEpisodicItem[];
	}
	: T extends DEPARTMENTS.PR
	? {
		EpisodicProp: EpisodicProp[];
		PrWrapBoxEpisodicProp: PrWrapBoxEpisodicProp[];
	}
	: T extends DEPARTMENTS.SET
	? {
		SetPiece: SetPiece[];
		SetPieceSet: SetPieceSet[];
		SetWrapBoxSetPiece: SetWrapBoxSetPiece[];
	}
	: never;
}

export type InventoryCreateResponse = DeptSpecificInventoryCreateResponse<
	InventoryDepartment
>;

export interface DeptSpecificWrapBoxInventoryRelationsResponse<
	T extends InventoryDepartment
	> extends SosListResponse {
	listed: T extends DEPARTMENTS.CM
	? {
		CmWrapBoxEpisodicItem: CmWrapBoxEpisodicItem[];
	}
	: T extends DEPARTMENTS.PR
	? {
		PrWrapBoxEpisodicProp: PrWrapBoxEpisodicProp[];
	}
	: T extends DEPARTMENTS.SET
	? {
		SetWrapBoxSetPiece: SetWrapBoxSetPiece[];
	}
	: never;
}

export type WrapBoxInventoryListRelationsResponse = DeptSpecificWrapBoxInventoryRelationsResponse<
	InventoryDepartment
>;

export interface DeptSpecificWrapBoxInventoryListResponse<
	T extends InventoryDepartment
	> extends SosListResponse {
	listed: T extends DEPARTMENTS.CM
	? {
		CmEpisodicItemPicture: CmEpisodicItemPicture[];
		CmWrapBoxEpisodicItem: CmWrapBoxEpisodicItem[];
		EpisodicCharacter: EpisodicCharacter[];
		EpisodicItem: EpisodicItem[];
		ChangeEpisodicItem: ChangeEpisodicItem[];
		Change: Change[];
	}
	: T extends DEPARTMENTS.PR
	? {
		EpisodicProp: EpisodicProp[];
		PrEpisodicPropPicture: PrEpisodicPropPicture[];
		PrWrapBoxEpisodicProp: PrWrapBoxEpisodicProp[];
		EpisodicCharacter: EpisodicCharacter[];
	}
	: T extends DEPARTMENTS.SET
	? {
		Set: Set[];
		SetPiece: SetPiece[];
		SetPieceSet: SetPieceSet[];
		SetPiecePicture: SetPiecePicture[];
		SetWrapBoxSetPiece: SetWrapBoxSetPiece[];
	}
	: never;
}

export type WrapBoxInventoryListResponse = DeptSpecificWrapBoxInventoryListResponse<
	InventoryDepartment
>;

export interface InventoryExportToNewSeasonReponse extends SosUpdateResponse {
	updated: {
		EpisodicItem: EpisodicItem[];
		EpisodicProp: EpisodicProp[];
		SetPiece: SetPiece[];
	};
}

export interface InventoryExportToNewSeasonCheckReponse {
	epItemIds: number[];
	epPropIds: number[];
	setPieceIds: number[];
	deptsWithoutPermsTo: DEPARTMENTS[];
	exportInProgress: boolean;
}

export interface InventoryQueryOptions {
	episodicCharacterIds?: number[];
	departments: DEPARTMENTS[];
	page?: number;
	pageSize?: number;
	prods: number[];
	search: string;
	searchColumns: string[];
	selectedColumns: string[];
	setIds?: number[];
	sortBy: string[];
	sortOrder: SortOrder[];
	complexIds?: string[];
}
